import React, { useState, useRef } from 'react';
import dayjs from 'dayjs';
import CustomDateRangePicker from '../../../../../components/CustomDateRangePicker';
import {
  StyledActivityCard,
  ActivityHeader,
  HeaderActions,
  Legend,
  LegendItem,
  LegendColor,
  ChartContainer,
  YAxis,
  GridContainer,
  GridLine,
  BarsWrapper,
  BarGroup,
  StackedBar,
  BarSegment,
  XAxis,
  XLabel,
  Tooltip,
  TooltipRow,
  TooltipDot
} from './style';
import { useSelector, useDispatch } from 'react-redux';
import { selectOrgDashboardData, fetchOrgWeeklyActivity } from '../../../../../modules/orgDashboard';
import { AppDispatch } from '../../../../../store';

const WeeklyWorkerActivity: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const dashboardData = useSelector(selectOrgDashboardData);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    dayjs().startOf('week'),
    dayjs().endOf('week')
  ]);
  const chartRef = useRef<HTMLDivElement>(null);

  const handleDateChange = (newRange: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
    setDateRange(newRange);
    if (newRange[0] && newRange[1]) {
      dispatch(fetchOrgWeeklyActivity({
        start_date: newRange[0].format('YYYY-MM-DD'),
        end_date: newRange[1].format('YYYY-MM-DD')
      }));
    }
  };

  const data = dashboardData.weeklyActivity || [
    { day: 'MON', date: 'mon', present: 0, absent: 0 },
    { day: 'TUE', date: 'tue', present: 0, absent: 0 },
    { day: 'WED', date: 'wed', present: 0, absent: 0 },
    { day: 'THU', date: 'thu', present: 0, absent: 0 },
    { day: 'FRI', date: 'fri', present: 0, absent: 0 },
    { day: 'SAT', date: 'sat', present: 0, absent: 0 },
    { day: 'SUN', date: 'sun', present: 0, absent: 0 }
  ];

  const maxDataVal = Math.max(...data.map(d => d.present + d.absent), 10);
  const maxVal = Math.ceil(maxDataVal / 5) * 5;
  const ySteps = Array.from({ length: 6 }, (_, i) => maxVal - (i * (maxVal / 5)));

  const presentColor = '#5E8E7E';
  const absentColor = '#c94c4c';

  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    if (!chartRef.current) return;
    
    const barRect = e.currentTarget.getBoundingClientRect();
    const chartRect = chartRef.current.getBoundingClientRect();
    
    const item = data[index];
    const totalValue = item.present + item.absent;
    const gridHeight = 250; // Adjusted for 30px label space
    const barPixels = (totalValue / maxVal) * gridHeight;
    
    setTooltipPos({
      x: barRect.left - chartRect.left + barRect.width / 2,
      y: gridHeight - barPixels + 10
    });
    
    setHoveredDay(index);
  };

  return (
    <StyledActivityCard>
      <ActivityHeader>
        <h2>Weekly Worker Activity</h2>
        <HeaderActions>
          <Legend>
            <LegendItem>
              <LegendColor color={presentColor} />
              Workers present
            </LegendItem>
            <LegendItem>
              <LegendColor color={absentColor} />
              Workers absent
            </LegendItem>
          </Legend>
          <CustomDateRangePicker
            value={dateRange}
            onChange={handleDateChange}
            mode="week"
          />
        </HeaderActions>
      </ActivityHeader>

      <ChartContainer ref={chartRef}>
        <YAxis>
          {ySteps.map(step => <span key={step}>{step}</span>)}
        </YAxis>
        
        <GridContainer>
          {ySteps.map((step, i) => (
            <GridLine key={step} bottom={`${(i / (ySteps.length - 1)) * 100}%`} />
          ))}
          
          <BarsWrapper>
            {data.map((item, index) => {
              const totalHeight = ((item.present + item.absent) / maxVal) * 100;
              const presentHeight = (item.present / (item.present + item.absent || 1)) * 100;
              const absentHeight = (item.absent / (item.present + item.absent || 1)) * 100;
              const isActive = hoveredDay === index;

              return (
                <BarGroup 
                  key={item.date}
                  onMouseMove={(e) => handleMouseMove(e, index)}
                  onMouseLeave={() => setHoveredDay(null)}
                >
                  <StackedBar style={{ height: `${totalHeight}%` }}>
                    <BarSegment 
                      height={`${presentHeight}%`} 
                      color={presentColor} 
                      active={isActive}
                    />
                    <BarSegment 
                      height={`${absentHeight}%`} 
                      color={absentColor} 
                      active={isActive}
                    />
                  </StackedBar>
                </BarGroup>
              );
            })}
          </BarsWrapper>
        </GridContainer>

        <XAxis>
          {data.map(item => <XLabel key={item.date}>{item.day}</XLabel>)}
        </XAxis>

        {hoveredDay !== null && (
          <Tooltip 
            left={tooltipPos.x} 
            top={tooltipPos.y} 
            visible={hoveredDay !== null}
          >
            <h4>{data[hoveredDay].day === 'FRI' ? 'FRIDAY' : data[hoveredDay].day}</h4>
            <TooltipRow>
              <div className="label-group">
                <TooltipDot color={presentColor} />
                Present :
              </div>
              {data[hoveredDay].present}
            </TooltipRow>
            <TooltipRow>
              <div className="label-group">
                <TooltipDot color={absentColor} />
                Absent :
              </div>
              {data[hoveredDay].absent}
            </TooltipRow>
          </Tooltip>
        )}
      </ChartContainer>
    </StyledActivityCard>
  );
};

export default WeeklyWorkerActivity;
